// Application-wide link from the Permtang web app to the Windows Permtang Helper: tells it which branch is signed in and
// whether that branch has Service Rating enabled, from ANY authenticated page and whether or not the Customer Display is
// open. (The Customer Display's own, token-protected bridge in shared/customer-display/helperBridge.js is a different
// thing: it carries the rating remote's key presses and is only ever handed to a window the Helper itself launched.)
//
// Protocol (see customer-display-helper/lib/AppSync.psm1, which validates every field):
//   Helper -> page   { v:1, type:'hello', app:'permtang-helper', version }       once, when the socket opens
//   Helper -> page   { v:1, type:'config-request' }                              "state your setting again" (its check button)
//   page   -> Helper { v:1, type:'service-rating-config', tabId, seq, visible,
//                      authenticated:true,  branchKey, enabled: true|false|null }
//   page   -> Helper { v:1, type:'service-rating-config', tabId, seq, visible, authenticated:false, branchKey }  (sign-out)
// `enabled: null` = signed in but this branch's settings are not loaded yet (never "off"). `branchKey` is an opaque hash of
// the branch id used only for equality (switch / sign-out bookkeeping): the id itself, tokens and cookies never leave the page.
//
// No token is involved: the Helper accepts the connection only from the allow-listed web origin (browsers always send the
// real Origin) and only on loopback; the message can set nothing but a status line in the Helper. A Helper that is not
// running simply means every connect attempt fails quietly and is retried with a growing delay.
//
// Lifecycle: one instance per tab, started once at application level (helperSyncInstall.js) and kept across route changes.
// Updates are event driven (sign-in, branch switch, setting change, tab visible, Helper asks); there is no polling while
// connected. While no Helper is found it probes its few ports at most every RETRY_MAX_MS.

export const HELPER_SYNC_PORT_START = 47615;
export const HELPER_SYNC_PORT_COUNT = 5;
export const RETRY_BASE_MS = 1000;
export const RETRY_MAX_MS = 30000;
// A Helper answers `hello` right after the upgrade; a port that stays silent this long is not a Helper.
export const HELLO_TIMEOUT_MS = 2500;

// Small synchronous, non-reversible-in-practice hash (two FNV-style passes -> 16 hex chars). Equality only; not a secret.
export function branchKeyOf(branchId) {
  const s = String(branchId || '');
  if (!s) return '';
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193 ^ s.length;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ (c + i), 0x85ebca6b) >>> 0;
  }
  const hex = (n) => ('00000000' + n.toString(16)).slice(-8);
  return hex(h1) + hex(h2);
}

function randomTabId(w) {
  try {
    const bytes = new Uint8Array(8);
    (w.crypto || globalThis.crypto).getRandomValues(bytes);
    return Array.from(bytes, (b) => ('0' + b.toString(16)).slice(-2)).join('');
  } catch (e) {
    return Math.random().toString(16).slice(2, 18).padEnd(16, '0');
  }
}

// deps:
//   getSnapshot() -> { authenticated: boolean, branchId: string, enabled: true|false|null }
//   win           (default window): needs WebSocket, setTimeout/clearTimeout, document (visibilityState + events)
//   ports         (default the Helper's range) -- overridable for tests
export function createHelperSync(deps) {
  const w = deps.win || (typeof window !== 'undefined' ? window : null);
  const getSnapshot = deps.getSnapshot;
  const ports = deps.ports || Array.from({ length: HELPER_SYNC_PORT_COUNT }, (_, i) => HELPER_SYNC_PORT_START + i);
  if (!w || typeof w.WebSocket !== 'function') {
    return { enabled: false, notify() {}, stop() {}, status: () => 'unsupported' };
  }

  const tabId = randomTabId(w);
  let socket = null;
  let portIndex = 0;
  let attempt = 0; // consecutive failed rounds over all ports (drives the backoff)
  let retryTimer = null;
  let helloTimer = null;
  let connected = false; // hello received on the current socket
  let seq = 0;
  let stopped = false;
  let lastSentKey = ''; // what the Helper was last told on this connection
  let lastBranchKey = ''; // the branch last signed in (named in the sign-out message)
  let status = 'searching'; // 'searching' | 'connected' | 'stopped'

  const isVisible = () => !w.document || w.document.visibilityState !== 'hidden';

  function message() {
    const snap = getSnapshot() || {};
    const base = { v: 1, type: 'service-rating-config', tabId, seq, visible: isVisible() };
    if (snap.authenticated && snap.branchId) {
      const branchKey = branchKeyOf(snap.branchId);
      lastBranchKey = branchKey;
      return { ...base, authenticated: true, branchKey, enabled: typeof snap.enabled === 'boolean' ? snap.enabled : null };
    }
    if (snap.authenticated === null || snap.authenticated === undefined) return null; // the page does not know yet (still starting): say nothing
    if (snap.authenticated) return null; // signed in, branch not known yet: nothing truthful to say
    return { ...base, authenticated: false, branchKey: lastBranchKey };
  }

  function stateKey(m) {
    return m ? [m.authenticated, m.branchKey, m.enabled, m.visible].join('|') : '';
  }

  function send(force) {
    if (!connected || !socket || socket.readyState !== w.WebSocket.OPEN) return false;
    const m = message();
    if (!m) return false;
    const key = stateKey(m);
    if (!force && key === lastSentKey) return false;
    try {
      m.seq = seq;
      socket.send(JSON.stringify(m));
      seq += 1;
      lastSentKey = key;
      return true;
    } catch (e) {
      return false;
    }
  }

  function clearTimers() {
    if (retryTimer) w.clearTimeout(retryTimer);
    if (helloTimer) w.clearTimeout(helloTimer);
    retryTimer = null;
    helloTimer = null;
  }

  function scheduleRetry() {
    if (stopped || retryTimer) return;
    const delay = Math.min(RETRY_BASE_MS * Math.pow(2, attempt), RETRY_MAX_MS);
    attempt += 1;
    retryTimer = w.setTimeout(() => {
      retryTimer = null;
      portIndex = 0;
      connect(); // eslint-disable-line no-use-before-define
    }, delay);
  }

  function nextPortOrRetry() {
    portIndex += 1;
    if (portIndex < ports.length) connect(); // eslint-disable-line no-use-before-define
    else scheduleRetry();
  }

  function connect() {
    if (stopped || connected) return;
    let s;
    try {
      s = new w.WebSocket(`ws://127.0.0.1:${ports[portIndex]}/`);
    } catch (e) {
      nextPortOrRetry();
      return;
    }
    socket = s;
    let opened = false;
    s.onopen = () => {
      opened = true;
      helloTimer = w.setTimeout(() => {
        try { s.close(); } catch (e) { /* ignore */ }
      }, HELLO_TIMEOUT_MS);
    };
    s.onmessage = (event) => {
      let msg = null;
      try { msg = JSON.parse(event.data); } catch (e) { return; }
      if (!msg || msg.v !== 1) return;
      if (msg.type === 'hello' && msg.app === 'permtang-helper' && !connected) {
        connected = true;
        status = 'connected';
        attempt = 0;
        seq = 0;
        lastSentKey = '';
        if (helloTimer) { w.clearTimeout(helloTimer); helloTimer = null; }
        send(true);
      } else if (msg.type === 'config-request' && connected) {
        send(true); // the Helper's "check" button: state it again
      }
    };
    s.onclose = () => {
      if (helloTimer) { w.clearTimeout(helloTimer); helloTimer = null; }
      const wasConnected = connected;
      if (socket === s) { socket = null; connected = false; status = 'searching'; }
      if (stopped) return;
      if (wasConnected) { attempt = 0; scheduleRetry(); } // the Helper went away (or restarted): look for it again
      else if (opened) scheduleRetry(); // something answered but was not a Helper
      else nextPortOrRetry(); // nothing on this port
    };
    s.onerror = () => { /* the close handler follows; nothing to report */ };
  }

  const onVisibility = () => {
    if (connected) send(false);
    else if (isVisible() && !socket && !retryTimer) { portIndex = 0; connect(); }
  };
  if (w.document && w.document.addEventListener) w.document.addEventListener('visibilitychange', onVisibility);

  connect();

  return {
    enabled: true,
    tabId,
    status: () => status,
    // Something that may change what the Helper should know happened (sign-in/out, branch, setting): tell it if it did.
    notify() { send(false); },
    stop() {
      stopped = true;
      status = 'stopped';
      clearTimers();
      if (w.document && w.document.removeEventListener) w.document.removeEventListener('visibilitychange', onVisibility);
      const s = socket;
      socket = null;
      connected = false;
      if (s) { try { s.onclose = null; s.close(); } catch (e) { /* ignore */ } }
    }
  };
}
